import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { addTag, archiveEntry, deleteAttachment, downloadAttachment, downloadEntry, getEntry, removeTag, updateTodoAssignee, updateTodoDueDate, updateTodoStatus } from '../../api/entries'
import type { AttachmentInfo, Entry } from '../../api/entries'
import { AvatarImage } from '../AvatarImage'
import { useAuthStore } from '../../store/auth'
import { formatDateTime, isOverdue } from '../../utils/dates'
import { AuthenticatedMedia } from './AuthenticatedMedia'
import { MarkdownRenderer } from './MarkdownRenderer'
import { ErrorBoundary } from '../ErrorBoundary'
import { TagInput } from './TagInput'

const PRIVACY_LABELS = ['private', 'friends', 'friends-of-friends', 'public']

function ReplyBanner({ replyToId }: { replyToId: string }) {
  const parts = replyToId.split('/')
  const parentUsername = parts[0]
  const parentDate = parts[1]
  const [parentEntry, setParentEntry] = useState<Entry | null>(null)

  useEffect(() => {
    getEntry(replyToId).then(setParentEntry).catch(() => {})
  }, [replyToId])

  return (
    <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
      ↩ reply to{' '}
      <Link to={`/~${parentUsername}/entry/${replyToId}`}>
        {parentEntry ? parentEntry.name : replyToId}
      </Link>
      {' · '}
      <Link to={`/~${parentUsername}`}>~{parentUsername}</Link>
      {' · '}
      {parentEntry ? formatDateTime(parentEntry.created_at) : parentDate}
    </div>
  )
}


const ENTRY_ERROR_FALLBACK = (
  <article style={{ border: '1px solid var(--color-fg)', padding: '0.75rem', opacity: 0.5, marginBottom: '1rem' }}>
    entry unavailable
  </article>
)

interface Props {
  entry: Entry
  onArchived?: (entryId: string) => void
  hideReplyContext?: boolean
}

function EntryCardInner({ entry: initialEntry, onArchived, hideReplyContext }: Props) {
  const [entry, setEntry] = useState(initialEntry)
  const [downloading, setDownloading] = useState(false)
  const currentUser = useAuthStore((s) => s.username)
  const isOwner = currentUser === entry.username
  const navigate = useNavigate()

  const [todoEditingAssignee, setTodoEditingAssignee] = useState(false)
  const [todoAssigneeInput, setTodoAssigneeInput] = useState('')

  async function handleTodoStatusChange(status: string) {
    try {
      const updated = await updateTodoStatus(entry.id, status)
      setEntry(updated)
    } catch { /* ignore */ }
  }

  async function submitTodoAssignee(e: React.FormEvent) {
    e.preventDefault()
    try {
      const updated = await updateTodoAssignee(entry.id, todoAssigneeInput.trim() || null)
      setEntry(updated)
      setTodoEditingAssignee(false)
    } catch { /* ignore */ }
  }

  async function handleTodoDueDateChange(dueDate: string | null) {
    try {
      const updated = await updateTodoDueDate(entry.id, dueDate)
      setEntry(updated)
    } catch { /* ignore */ }
  }

  const handleMarkdownUpdate = useCallback((newContent: string) => {
    setEntry((e) => ({ ...e, content: newContent }))
  }, [])

  // tag state
  const [tags, setTags] = useState<{ name: string; tagger_username: string }[]>(entry.tags ?? [])

  const [attachments, setAttachments] = useState<AttachmentInfo[]>(entry.attachments ?? [])

  const { mimetype } = entry
  const isMedia = mimetype.startsWith('image/') || mimetype.startsWith('video/') || mimetype.startsWith('audio/')
  const isBinary = !mimetype.startsWith('text/')

  const ct = entry.color_theme
  const themeVars: React.CSSProperties = ct
    ? ({
        '--color-bg': ct.bg,
        '--color-fg': ct.fg,
        '--color-1': ct.color1,
        '--color-2': ct.color2,
      } as React.CSSProperties)
    : {}
  const bodyStyle: React.CSSProperties = ct
    ? { background: ct.bg, color: ct.fg, padding: '0.75rem', borderRadius: 2, margin: '0.75rem 0' }
    : { margin: '0.75rem 0' }

  async function handleArchive() {
    if (!window.confirm(`archive "${entry.name || 'untitled'}"?`)) return
    try {
      await archiveEntry(entry.id)
      onArchived?.(entry.id)
    } catch { /* ignore */ }
  }

  async function handleAddTag(tag: string) {
    if (!currentUser) return
    try {
      await addTag(entry.id, tag)
      setTags((prev) => prev.some((t) => t.name === tag && t.tagger_username === currentUser) ? prev : [...prev, { name: tag, tagger_username: currentUser }])
    } catch { /* ignore */ }
  }

  async function handleRemoveTag(tagName: string) {
    try {
      await removeTag(entry.id, tagName)
      setTags((prev) => prev.filter((t) => !(t.name === tagName && t.tagger_username === currentUser)))
    } catch { /* ignore */ }
  }

  const date = formatDateTime(entry.created_at)
  const isEdited = (entry.version_num ?? 0) > 1

  return (
    <article style={{ ...themeVars, borderBottom: '1px solid var(--color-fg)', padding: '1rem 0 1.5rem' }}>
      {entry.reply_to_entry_id && !hideReplyContext && (
        <ReplyBanner replyToId={entry.reply_to_entry_id} />
      )}

      <>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: '0.4rem', margin: '0.5rem 0' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
              <Link to={`/~${entry.username}/entry/${entry.id}`}>{entry.name || <em>untitled</em>}</Link>
            </h3>
            <header style={{ display: 'flex', alignItems: 'center', opacity: 0.6, fontSize: '0.8rem', marginTop: '0.25rem' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {entry.avatar_entry_id && <AvatarImage entryId={entry.avatar_entry_id} size={20} />}
                <Link to={`/~${entry.username}`}>~{entry.username}</Link>
                {' · '}
                {date}
                {isEdited && <>{' · '}<span title={formatDateTime(entry.updated_at)}>edited</span></>}
              </span>
            </header>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem', fontSize: '0.8rem', flexShrink: 0, alignSelf: 'center' }}>
            {entry.entry_type === 'todo' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.7 }}>
                {todoEditingAssignee ? (
                  <form onSubmit={submitTodoAssignee} style={{ display: 'inline-flex', gap: '0.3rem' }}>
                    <input
                      value={todoAssigneeInput}
                      onChange={(e) => setTodoAssigneeInput(e.target.value)}
                      placeholder="username"
                      style={{ fontSize: '0.8rem', padding: '0.1rem 0.3rem', width: 120 }}
                      autoFocus
                    />
                    <button type="submit" style={{ fontSize: '0.75rem', width: 'auto', padding: '0.1rem 0.4rem' }}>ok</button>
                    <button type="button" onClick={() => setTodoEditingAssignee(false)} style={{ fontSize: '0.75rem', background: 'transparent', color: 'var(--color-fg)', width: 'auto', padding: '0.1rem 0.2rem' }}>×</button>
                  </form>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {entry.todo_assignee_username && (
                      <Link to={`/~${entry.todo_assignee_username}`}>~{entry.todo_assignee_username}</Link>
                    )}
                    {isOwner && (
                      <button
                        type="button"
                        onClick={() => { setTodoAssigneeInput(entry.todo_assignee_username ?? ''); setTodoEditingAssignee(true) }}
                        style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.75rem', padding: '0 0.15rem', opacity: 0.5 }}
                      >
                        {entry.todo_assignee_username ? 'edit' : 'assign'}
                      </button>
                    )}
                  </span>
                )}
                {isOwner ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    {entry.todo_due_date && (
                      <span style={{ color: isOverdue(entry.todo_due_date, entry.todo_status) ? 'var(--color-1)' : 'inherit', opacity: isOverdue(entry.todo_due_date, entry.todo_status) ? 1 : 0.7 }}>
                        due {entry.todo_due_date}
                      </span>
                    )}
                    <input
                      type="date"
                      value={entry.todo_due_date ?? ''}
                      onChange={(e) => handleTodoDueDateChange(e.target.value || null)}
                      aria-label="due date"
                      style={{ width: 'auto', fontSize: '0.75rem', padding: '0 0.2rem' }}
                    />
                  </span>
                ) : (
                  entry.todo_due_date && (
                    <span style={{ color: isOverdue(entry.todo_due_date, entry.todo_status) ? 'var(--color-1)' : 'inherit' }}>
                      due {entry.todo_due_date}
                    </span>
                  )
                )}
                {isOwner ? (
                  <select
                    value={entry.todo_status ?? 'open'}
                    onChange={(e) => handleTodoStatusChange(e.target.value)}
                    style={{ width: 'auto', fontSize: '0.8rem' }}
                  >
                    {['open', 'wip', 'done', 'cancelled'].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                ) : (
                  <span>{entry.todo_status ?? 'open'}</span>
                )}
              </div>
            )}
            <span style={{ opacity: 0.6 }}>[{PRIVACY_LABELS[entry.privacy] ?? entry.privacy}]</span>
          </div>
        </div>
        {attachments.filter((a) => a.mimetype.startsWith('image/') || a.mimetype.startsWith('audio/') || a.mimetype.startsWith('video/')).map((att) => (
          <div key={att.id} style={{ marginTop: '0.25rem' }}>
            <AuthenticatedMedia
              url={`/api/v1/entries/${encodeURIComponent(entry.id)}/attachments/${encodeURIComponent(att.id)}`}
              mimetype={att.mimetype}
              name={att.filename}
            />
          </div>
        ))}
        {isBinary ? (
          <div style={{ marginTop: '0.25rem' }}>
            {isMedia ? (
              <AuthenticatedMedia entryId={entry.id} mimetype={mimetype} name={entry.name || 'untitled'} />
            ) : (
              <button
                type="button"
                disabled={downloading}
                onClick={async () => {
                  setDownloading(true)
                  await downloadEntry(entry.id, entry.name || 'untitled').catch(() => {})
                  setDownloading(false)
                }}
                style={{ width: 'auto', fontSize: '0.85rem' }}
              >
                {downloading ? 'downloading…' : `download (${mimetype})`}
              </button>
            )}
          </div>
        ) : entry.content ? (
          <div style={bodyStyle}>
            {mimetype === 'text/markdown' ? (
              <MarkdownRenderer
                entryId={entry.id}
                authorUsername={entry.username}
                content={entry.content}
                colorTheme={entry.color_theme}
                onUpdate={handleMarkdownUpdate}
              />
            ) : (
              <pre style={{ opacity: 0.8, fontSize: '0.9rem', overflow: 'auto' }}>{entry.content}</pre>
            )}
          </div>
        ) : null}
        {attachments.filter((a) => !a.mimetype.startsWith('image/') && !a.mimetype.startsWith('audio/') && !a.mimetype.startsWith('video/')).map((att) => (
          <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginTop: '0.3rem' }}>
            <button
              type="button"
              onClick={() => downloadAttachment(entry.id, att.id, att.filename).catch(() => {})}
              style={{ width: 'auto', fontSize: '0.85rem' }}
            >
              download {att.filename}
            </button>
            {isOwner && (
              <button
                type="button"
                onClick={async () => {
                  await deleteAttachment(entry.id, att.id).catch(() => {})
                  setAttachments((prev) => prev.filter((a) => a.id !== att.id))
                }}
                style={{ background: 'none', border: 'none', color: 'var(--color-fg)', cursor: 'pointer', padding: '0 0.2rem', lineHeight: 1, fontSize: '1rem', opacity: 0.6 }}
                aria-label={`remove attachment ${att.filename}`}
              >×</button>
            )}
          </div>
        ))}
      </>

      {(tags.length > 0 || currentUser) && (
        <div style={{ marginTop: '0.75rem' }}>
          {currentUser && (
            <TagInput
              username={currentUser}
              onSubmit={handleAddTag}
              excludeTags={tags.filter((t) => t.tagger_username === currentUser).map((t) => t.name)}
              inputStyle={{ fontSize: '0.85rem' }}
              buttonStyle={{ width: 'auto', fontSize: '0.85rem' }}
            />
          )}
          {tags.length > 0 && (
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.5rem', marginBottom: '0.75rem' }}>
              {tags.map((tag) => (
                <span key={`${tag.tagger_username}:${tag.name}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.1rem', background: 'var(--color-fg)', color: 'var(--color-bg)', padding: '0.1rem 0.4rem', borderRadius: 2, fontSize: '0.85rem' }}>
                  <Link to={`/~${tag.tagger_username}/tags/${encodeURIComponent(tag.name)}`} style={{ color: 'inherit', textDecoration: 'none' }}>#{tag.name}</Link>
                  {currentUser === tag.tagger_username && (
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag.name)}
                      style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0.1rem', lineHeight: 1, fontSize: '1rem' }}
                      aria-label={`remove tag ${tag.name}`}
                    >×</button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {isOwner && (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => navigate(`/~${entry.username}/entry/${entry.id}?edit=1`)}
            style={{ width: 'auto' }}
          >
            edit
          </button>
          <button type="button" onClick={handleArchive} className="btn-inverse" style={{ width: 'auto', marginLeft: 'auto' }}>
            archive
          </button>
        </div>
      )}
    </article>
  )
}

export function EntryCard(props: Props) {
  return (
    <ErrorBoundary fallback={ENTRY_ERROR_FALLBACK}>
      <EntryCardInner {...props} />
    </ErrorBoundary>
  )
}
